import { Component, ErrorInfo, ReactNode } from 'react';
import i18n from '../../i18n';

interface Props {
  children: ReactNode;
  pageName?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Per-route error boundary — catches crashes within a single page
 * while keeping the sidebar, header, and router intact.
 *
 * This prevents the "fragile navigation state" issue where a crash on
 * one page (e.g. Daily Log) would take down the entire app and lose
 * track of the active cat, forcing the user into an onboarding loop.
 */
export class RouteErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(`RouteErrorBoundary [${this.props.pageName ?? 'unknown'}]:`, error, info);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      const { pageName } = this.props;

      return (
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="max-w-md text-center">
            <div className="w-14 h-14 mx-auto mb-4 bg-amber-50 rounded-full flex items-center justify-center">
              <svg
                className="w-7 h-7 text-amber-600"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" x2="12" y1="8" y2="12" />
                <line x1="12" x2="12.01" y1="16" y2="16" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              {pageName !== undefined
                ? i18n.t('shared.routeErrorBoundary.heading', { pageName })
                : i18n.t('shared.routeErrorBoundary.headingFallback')}
            </h2>
            <p className="text-sm text-gray-600 mb-6 leading-relaxed">
              {i18n.t('shared.routeErrorBoundary.body')}
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={this.handleRetry}
                className="px-4 py-2.5 text-sm font-medium text-sage-700 bg-sage-50 hover:bg-sage-100 rounded-lg transition-colors min-h-[44px]"
              >
                {i18n.t('shared.errorRecovery.tryAgain')}
              </button>
              <a
                href="/dashboard"
                className="px-4 py-2.5 text-sm font-medium text-white bg-sage-600 hover:bg-sage-700 rounded-lg transition-colors min-h-[44px] inline-flex items-center"
              >
                {i18n.t('shared.errorRecovery.goToDashboard')}
              </a>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
